import CAIconComponent from '@ds/components/shared/CAIconComponent'
import { defineComponent, type PropType } from 'vue'

import type { CAComponentMode, CADividerType } from '../interfaces'

/**
	* CADivider component.
	* Renders a divider line with optional text.
	*
	* @component CADivider
	*
	* @prop {CAComponentMode} [mode=''] - The mode of the divider, can be 'dark' or 'light'.
	* @prop {string} [title=''] - The title to display on the divider.
	* @prop {CADividerType} [type='default'] - The type of divider, can be 'default', 'title', 'title-left', 'add', or 'vertical'.
	*
	* @slot customTitle - Custom title slot for the divider.
	* @slot textRight - Slot for additional text on the right side of the divider.
	*
*/

export default defineComponent({
	name: 'CADivider',

	props: {
		/**
		 * The mode of the divider determines its appearance
		 *
		 * @default ''
		 * @type {CAComponentMode}
		 * @enum {('' | 'dark' | 'light')}
		 */
		mode: { default: '', type: String as PropType<CAComponentMode> },
		/**
		 *
		 * The title to display on the divider.
		 *
		 * @default ''
		 * @type {string}
		 */
		title: { default: '', type: String },
		/**
		 * The type of divider determines its style and behavior"
		 *
		 * @default 'default'
		 * @type {CADividerType}
		 * @enum {('default' | 'title' | 'title-left' | 'add' | 'vertical')}
		 */
		type: { default: 'default', type: String as PropType<CADividerType> },
	},

	render() {
		return (
			<div
				class={`ca-divider ca-divider--${this.mode}`}
				data-test="ca-divider"
				data-test-component="design-system-divider"
			>
				<div aria-hidden="true" class="ca-divider__line-wrapper">
					<div
						class={[
							'ca-divider__line',
							{
								'ca-divider__line--default': this.type === 'default',
								'ca-divider__line--vertical': this.type === 'vertical',
							},
						]}
					/>
				</div>

				{[
					'add',
					'title-left',
					'title',
				].includes(this.type) &&
					<div
						class={[
							'ca-divider__text-wrapper',
							{ 'ca-divider__text-wrapper--title-left': this.type === 'title-left' },
						]}
					>
						{this.title && this.type !== 'add' ?
							<span
								class={[
									'ca-divider__text',
									{
										'ca-divider__text--left': this.type === 'title-left',
										'ca-divider__text--center': this.type !== 'title-left',
									},
								]}
							>
								{this.title}
							</span>

							: !this.title && this.type !== 'add' && this.$slots.customTitle?.() ?
								<span
									class={[
										'ca-divider__text',
										{ 'ca-divider__text--left': this.type === 'title-left' },
									]}
								>
									{this.$slots.customTitle?.()}
								</span>

								:

								null}

						{this.type === 'title-left' && this.$slots.textRight?.() &&
							<span class="ca-divider__text ca-divider__text--right">
								{this.$slots.textRight?.()}
							</span>
						}

						{this.type === 'add' &&
							<CAIconComponent class="ca-divider__text--add-wrapper ca-divider__text--add" icon="SVGPlus" />
						}
					</div>
				}
			</div>
		)
	},
})
